import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { EfileWorkflowService } from '../return-review/efile-workflow.service';
import { DfSelectSingleOption } from '../../../shared/_directives/df-select-single/df-select-single-filter.model';
import {
  IrsXmlCompareApiResponse,
  IrsXmlCompareRow,
  XmlCompareExportHeader,
} from './xml-compare.util';

@Injectable({ providedIn: 'root' })
export class EfileXmlCompareService {
  private readonly http = inject(HttpClient);
  private readonly workflowService = inject(EfileWorkflowService);



  loadIrsSubmissionOptions(
    baseUrl: string,
    submissionId: string | number,
    globalService: any,
  ): Observable<DfSelectSingleOption[]> {
    return this.workflowService.loadIrsSubmissions(baseUrl, submissionId, globalService).pipe(
      map((res) => (Array.isArray(res?.jsonObject) ? res.jsonObject : []) as DfSelectSingleOption[]),
      catchError(() => of([])),
    );
  }

  compareIrsXml(
    efileBaseUrl: string,
    submissionId: string | number,
    irsId1: string,
    irsId2: string,
  ): Observable<IrsXmlCompareApiResponse> {
    return this.http
      .get<IrsXmlCompareApiResponse>(`${efileBaseUrl}/compareIrsXml`, {
        params: {
          submissionId: String(submissionId ?? ''),
          irsId1: String(irsId1 ?? ''),
          irsId2: String(irsId2 ?? ''),
        },
        withCredentials: true,
      })
      .pipe(
        map((res) => {
          if (String(res?.callSuccess) !== '1') {
            throw new Error(res?.errorMessage || 'IRS XML comparison failed.');
          }
          return res;
        }),
        catchError((err) => {
          const message =
            err?.error?.errorMessage ||
            (err instanceof Error ? err.message : null) ||
            'IRS XML comparison failed. One of the selected files may be unavailable or invalid.';
          return throwError(() => new Error(message));
        }),
      );
  }



  /**
   * Option A export path:
   * POST /exportToExcel with multipart filtered payload (no action_id/grid_id).
   * `is_compressed=false` — file must be a JSON array of row objects; compressed
   * mode expects `{ header: [[maps...]], data: [[values...]] }` instead.
   */
  exportCompareRowsToExcel(
    gtwBaseUrl: string,
    screenKey: string,
    headers: XmlCompareExportHeader[],
    rows: IrsXmlCompareRow[],
    ssoId: string,
  ): Observable<void> {
    const formData = new FormData();
    formData.append('filtered_export', 'true');
    formData.append('is_compressed', 'false');
    formData.append('screen_key', String(screenKey || ''));
    formData.append('headerArray', JSON.stringify(headers || []));
    formData.append(
      'file',
      new Blob([JSON.stringify(rows || [])], { type: 'application/json' }),
      'filteredGridData.json',
    );

    return this.http
      .post<any>(`${gtwBaseUrl}/exportToExcel`, formData, { withCredentials: true })
      .pipe(
        switchMap((data) => this.completeExportDownload(data, gtwBaseUrl, ssoId)),
        catchError((err) => {
          const message =
            err?.error?.errorMessage ||
            err?.error?.ERROR ||
            (err instanceof Error ? err.message : null) ||
            'Error occurred while exporting. Please try again.';
          return throwError(() => new Error(message));
        }),
      );
  }

  private completeExportDownload(
    data: any,
    gtwBaseUrl: string,
    ssoId: string,
  ): Observable<void> {
    if (data?.ERROR) {
      return throwError(() => new Error(String(data.ERROR)));
    }
    if (String(data?.callSuccess) !== '1') {
      return throwError(() => new Error(
        data?.errorMessage || 'Data can not be exported, please contact System administrator',
      ));
    }

    const directUrl = data?.url;
    if (directUrl && String(directUrl).trim() !== '') {
      window.open(String(directUrl), '_blank');
      return of(undefined);
    }

    const fileName = data?.fileName || data?.file_name || '';
    if (!fileName) {
      return throwError(() => new Error('Export response did not include a file name.'));
    }

    return this.workflowService.getApiAuthToken(gtwBaseUrl).pipe(
      map((tokenRes) => {
        if (!tokenRes?.apiToken) {
          throw new Error('Failed to retrieve auth token for download');
        }
        const clientKey = sessionStorage.getItem('client_key') || '';
        const downloadUrl =
          `${gtwBaseUrl}/download?file_name=${encodeURIComponent(fileName)}`
          + `&client_key=${encodeURIComponent(clientKey)}`
          + `&apiToken=${encodeURIComponent(tokenRes.apiToken)}`
          + `&georaclehrid=${encodeURIComponent(ssoId)}`;
        window.open(downloadUrl, '_blank');
      }),
    );
  }

}

